import { useState, useRef, useEffect } from "react";
import I18n from "common/i18n";
import { localeToHtmlLang } from "common/utils/locale";
import { useScale, btnDelay, usePlaySound } from "common/utils";
import { Select, SelectOption } from "common/elements/Select/Select";
import { Footer, TitleSvg } from "features/home";
import { AuthSelection } from "features/auth/AuthSelection";
import { CreateGuestUser } from "features/auth/CreateGuestUser";

const LOCALE_OPTIONS: SelectOption<string>[] = [
  { value: "ja", label: "ひらがな" },
  { value: "ja_kanji", label: "漢字" },
  { value: "en", label: "English" },
  { value: "zh_tw", label: "繁體中文" },
];

const LOCALE_LABELS: Record<string, string> = {
  ja: "ひらがな",
  ja_kanji: "漢字",
  en: "English",
  zh_tw: "繁體中文",
};

/**
 * デバイス/ブラウザの言語設定に基づいてデフォルトロケールを取得する
 * - ネイティブアプリ: window.kidsAppHostLanguageCode を使用
 * - ウェブ環境: navigator.language を使用
 * - デフォルトは日本語(ja)
 */
const getDefaultLocale = (): string => {
  // 開発環境のみen/zh_twが利用可能
  const availableLocales = ["ja", "ja_kanji", "en", "zh_tw"];

  const getLocaleFromLangCode = (lang: string): string => {
    const langCode = lang.split("-")[0].toLowerCase();
    if (langCode === "en" && availableLocales.includes("en")) {
      return "en";
    }
    if (langCode === "zh" && availableLocales.includes("zh_tw")) {
      return "zh_tw";
    }
    return "ja";
  };

  // ネイティブアプリの場合は、デバイス言語設定を使用
  if (window.kidsAppHostLanguageCode) {
    return getLocaleFromLangCode(window.kidsAppHostLanguageCode);
  }

  // ウェブ環境の場合はブラウザの言語設定を使用
  return getLocaleFromLangCode(navigator.language || "");
};

export const Onboarding = () => {
  const TITLE_WIDTH = 828;
  const TITLE_HEIGHT = 182;
  const ITEM_HEIGHT = 450;
  const ITEM_TOP_MARGIN = 28;

  const play = usePlaySound();
  const { scale } = useScale();
  const [guest, setGuest] = useState(false);
  const [signUp, setSignUp] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [currentLocale, setCurrentLocale] = useState<string>(getDefaultLocale);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  // 初期ロード時にI18n.localeとCSSクラスを設定
  useEffect(() => {
    I18n.locale = currentLocale;

    const rootElement = document.documentElement;
    rootElement.className = rootElement.className
      .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
      .trim();
    if (currentLocale && !currentLocale.includes("ja")) {
      rootElement.classList.add(`lang-${currentLocale}`);
    }
    rootElement.lang = localeToHtmlLang(currentLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowGuest = () => {
    btnDelay(() => {
      play();
      setGuest(true);
    });
  };
  const handleCloseGuest = () => {
    btnDelay(() => {
      play();
      setGuest(false);
    });
  };

  const handleShowAuthSelection = () => {
    btnDelay(() => {
      play();
      setSignUp(true);
    });
  };
  const handleCloseAuthSelection = () => {
    btnDelay(() => {
      play();
      setSignUp(false);
    });
  };

  const handleToggleLocale = () => {
    btnDelay(() => {
      play();
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    I18n.locale = locale;
    setCurrentLocale(locale);

    const rootElement = document.documentElement;
    rootElement.className = rootElement.className
      .replace(/\blang-[a-z]{2}(_[a-z]{2})?\b/g, "")
      .trim();
    if (locale && !locale.includes("ja")) {
      rootElement.classList.add(`lang-${locale}`);
    }
    rootElement.lang = localeToHtmlLang(locale);
  };

  return (
    <div className="flex-col-el flex-center relative w-full h-full">
      <CreateGuestUser open={guest} close={handleCloseGuest} />

      <AuthSelection open={signUp} close={handleCloseAuthSelection} />

      <div
        className="flex-col-view self-center"
        style={{
          width: TITLE_WIDTH * scale,
          height: TITLE_HEIGHT * scale,
        }}
      >
        <div
          className="flex-col-view origin-top-left"
          style={{
            width: TITLE_WIDTH,
            height: TITLE_HEIGHT,
            transform: `scale(${scale})`,
          }}
        >
          <TitleSvg />
        </div>
      </div>

      <div
        className="flex-col-center w-full"
        style={{
          height: ITEM_HEIGHT * scale,
          marginTop: ITEM_TOP_MARGIN * scale,
        }}
      >
        <div className="flex-col-center gap-2 upsilon" style={{ transform: `scale(${scale})` }}>
          <button
            className="type-inherit-font btn btn-primary w-[680px] h-[250px] !rounded-3xl"
            onClick={handleShowAuthSelection}
          >
            <div className="flex-row-center gap-8 w-full h-full p-4 relative">
              <div className="flex-col-center">
                <div
                  className="flex-col-center bg-white rounded-full w-[150px] h-[150px] border-4 border-green-60"
                  style={{
                    boxShadow:
                      "0 6px 0 rgba(0, 0, 0, 0.12), 0 12px 24px rgba(43, 220, 152, 0.55), 0 0 24px rgba(43, 220, 152, 0.4)",
                  }}
                >
                  <span className="material-symbols-outlined type-display text-green-100">
                    emoji_events
                  </span>
                </div>
              </div>

              <div className="flex-col-el h-[150px] items-center justify-between">
                <p
                  className="type-text type-hero text-white"
                  style={{
                    textShadow: "0 2px 0 #1aa873, 0 4px 0 #16915f, 0 6px 12px rgba(0, 0, 0, 0.25)",
                  }}
                >
                  {I18n.t("MSG_ONBOARDING_PARENT_START")}
                </p>
                <div className="mx-auto w-[70%] flex items-center justify-between">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <span key={i} className="w-[7px] h-[7px] rounded-full bg-white/95" />
                  ))}
                </div>
                <p
                  className="type-text type-sm text-white whitespace-pre-line"
                  style={{
                    textShadow: "0 1px 0 #1aa873, 0 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {I18n.t("MSG_ONBOARDING_PARENT_START_SUB")}
                </p>
              </div>
            </div>
          </button>

          <button
            className="type-inherit-font btn btn-blue !bg-blue-80 w-[500px] h-[125px] !rounded-3xl"
            onClick={handleShowGuest}
          >
            <div className="flex-row-center gap-4 w-full h-full p-4 relative">
              <div className="flex-col-center">
                <div
                  className="flex-col-center bg-white rounded-xl w-[80px] h-[80px] shadow-lg border-4 border-blue-20"
                  style={{
                    boxShadow: "0 8px 16px rgba(76, 157, 241, 0.2)",
                  }}
                >
                  <span className="material-symbols-outlined type-title text-blue-100">
                    rocket_launch
                  </span>
                </div>
              </div>

              <div className="text-left flex-col gap-1">
                <p className="type-text type-body text-white">
                  {I18n.t("MSG_ONBOARDING_SELF_START")}
                </p>
                <p className="type-text type-sm text-white">
                  {I18n.t("MSG_ONBOARDING_SELF_START_SUB")}
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div
        className="flex-col-center origin-top"
        style={{ transform: `scale(${scale})`, marginTop: 48 * scale }}
      >
        <div className="flex-row-el items-center gap-3">
          <span className="text-[20px] text-textcolor-black w-[200px] text-right">
            {I18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
          </span>
          <button
            ref={langBtnRef}
            onClick={handleToggleLocale}
            className="border-0 bg-green-100 flex-row-el items-center gap-2 px-6 py-3 rounded-full backdrop-blur-sm shadow-md cursor-pointer transition-all w-[200px]"
          >
            <span className="material-symbols-outlined text-white !text-[28px]">language</span>
            <span className="text-body text-white text-[20px] flex-1 text-center">
              {LOCALE_LABELS[currentLocale] || LOCALE_LABELS["ja"]}
            </span>
            <span className="material-symbols-outlined text-white !text-[20px]">expand_more</span>
          </button>
        </div>
      </div>

      <Select
        anchorRef={langBtnRef}
        open={localeOpen}
        onClose={handleCloseLocale}
        value={currentLocale}
        onChange={handleChangeLocale}
        options={LOCALE_OPTIONS}
      />

      <Footer />
    </div>
  );
};
